/**
 * @file Integration Steps Configuration
 * @description Factory function that generates wizard step configurations based on
 * cloud provider and integration module. Different combinations may require different
 * steps (e.g., CIEM needs credentials, CSPM may not).
 */

import { StepConfig } from './index';
import { createIntegration } from '../../../services/integrationListingService';
import AWSCloudFormationStep from '../IntegrationSteps/AWSCloudFormation';
import AWSIntegrationDetailsStep from '../IntegrationSteps/AWSIntegrationDetailsStep';
import ExpirationSettingsStep from '../IntegrationSteps/ExpirationSettings';
import ReviewStep from '../IntegrationSteps/ReviewStep';
import { CloudProvider, IntegrationModule, IntegrationData } from '../types';

/**
 * Generates the appropriate wizard steps based on cloud provider and integration module
 *
 * This function dynamically builds the step configuration array based on the selected
 * cloud provider and integration module. Different combinations require different steps:
 * - All integrations: Account Details step
 * - CIEM only: Credentials step (requires API access)
 * - All integrations: Review and Submit step
 *
 * Each step includes:
 * - Component to render
 * - Validation logic
 * - Optional async operations (e.g., API calls)
 *
 * @param {CloudProvider} cloudProvider - Selected cloud provider (AWS, Azure, GCP, M365)
 * @param {IntegrationModule} integrationModule - Selected module (CSPM, CIEM)
 * @param {number} companyId - Company ID for API calls
 * @returns {StepConfig[]} Array of step configurations for the wizard
 *
 * @example
 * const steps = IntegrationStepsConfig(CloudProvider.AWS, IntegrationModule.CSPM, 123);
 * // Returns: [AccountDetailsStep, ReviewStep]
 *
 * @example
 * const steps = IntegrationStepsConfig(CloudProvider.AWS, IntegrationModule.CIEM, 123);
 * // Returns: [AccountDetailsStep, CredentialsStep, ReviewStep]
 */
const IntegrationStepsConfig = (
  cloudProvider: CloudProvider,
  integrationModule: IntegrationModule,
  companyId: number,
): StepConfig[] => {
  const steps: StepConfig[] = [];

  switch (cloudProvider) {
    case CloudProvider.AWS:
      // Step 1: Account Details - Required for both CSPM and CIEM
      if (
        integrationModule === IntegrationModule.CSPM ||
        integrationModule === IntegrationModule.CIEM
      ) {
        steps.push({
          id: 'integration-details',
          title: 'Integration Details',
          description: 'Configure your AWS account integration settings',
          component: AWSIntegrationDetailsStep,
          allowBack: false, // First step - no back navigation
        });
      }

      // Step 2: Credentials - Only required for CIEM (needs API access)
      if (integrationModule === IntegrationModule.CIEM) {
        steps.push({
          id: 'expiration-settings',
          title: 'Retention Settings',
          description: 'Configure Data Retention Settings for AWS',
          component: ExpirationSettingsStep,
          allowBack: true, // Can go back to previous step
          onNext: async (data: IntegrationData) => {
            // Simulate credential verification (replace with actual API call if needed)
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return data;
          },
        });
      }

      // Step 3: Review & Confirm
      if (
        integrationModule === IntegrationModule.CSPM ||
        integrationModule === IntegrationModule.CIEM
      ) {
        steps.push(
          {
            id: 'review',
            title: 'Review & Confirm',
            description: 'Review your integration settings before submission',
            component: ReviewStep,
            allowBack: true,
            onNext: async (data: IntegrationData) => {
              // Prepare payload for API call
              const payload = {
                company_id: companyId,
                module: integrationModule,
                provider: data.cloudProvider,
                label: data.integrationLabel,
                awsOrganizationId: data.accountId,
                integrationType: data.integrationType,
                cloudTrailExpiration: data.cloudTrailExpiration,
                cloudTrailNonCurrent: data.cloudTrailNonCurrent,
                athenaResultsExpiration: data.athenaResultsExpiration,
              };

              let res;
              try {
                res = await createIntegration(
                  payload as Record<string, unknown>,
                );
              } catch (err: any) {
                const errorMessage =
                  typeof err?.data?.error === 'string'
                    ? err.data.error
                    : err?.data?.error?.details ||
                      'An error occurred while creating the integration. Please try again.';
                throw new Error(errorMessage);
              }

              if (res.status === 200 || res.status === 201) {
                // Return data with CloudFormation Stack link from API response
                return {
                  ...data,
                  cfsLink: res.data.cfsLink,
                  integrationId: res.data.integration.id,
                };
              } else {
                throw new Error(
                  'An error occurred while creating the integration. Please try again.',
                );
              }
            },
          },
          {
            id: 'FormationStack',
            title: 'Deploy Stack & Provision Access',
            description:
              'Launch CloudFormation stack to complete AWS account integration',
            component: AWSCloudFormationStep,
            allowBack: true, // Can go back to previous step
          },
        );
      }
      break;

    default:
      // No steps configured for other cloud providers yet
      return [];
  }
  return steps;
};

export default IntegrationStepsConfig;
